'use client'

import Link from '@/components/common/Link'
import { LinkText, P } from '@/components/common/Typo'
import { selectPreviewMode } from '@/redux/customisation'
import { useTranslate } from '@tolgee/react'
import HTMLReactParser, {
  DOMNode,
  Element,
  HTMLReactParserOptions,
} from 'html-react-parser'
import domToReact from 'html-react-parser/lib/dom-to-react'
import React from 'react'
import { useSelector } from 'react-redux'
import DOMPurify from 'isomorphic-dompurify'

// Constants for text alignment and styling
const TEXT_ALIGN = {
  LEFT: 'text-left',
  CENTER: 'text-center',
  RIGHT: 'text-right',
  HIGHLIGHT_COLOR: 'highlight-color',
} as const

const LinkMark = ({ href, ...props }: { href: string }) => {
  const { t } = useTranslate()
  const previewMode = useSelector(selectPreviewMode)

  return (
    <Link
      {...props}
      href={href}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        if (!previewMode) return
        e.preventDefault()
        const confirmed = window.confirm(
          t(
            'This link cannot be opened in customisation preview. It will be opened in a new window ({target}).\nClick OK to continue',
            { target: href }
          )
        )
        if (confirmed) {
          window.open(href, '_blank')
        }
      }}
    />
  )
}
LinkMark.displayName = 'LinkMark'

// Rich text container component
const RichTextContainer: React.FC<{
  color?: string
  children: React.ReactNode
}> = ({ color, children }) => (
  <div className="rich-text-container whitespace-pre-wrap" style={{ color }}>
    {children}
  </div>
)
RichTextContainer.displayName = 'RichTextContainer'

// Component wrapper factory
function componentBlockWrapper(options: any, children: Element[]) {
  return function WrappedComponent(
    Component: React.ComponentType<any> | string,
    props = {}
  ) {
    return (
      <Component {...options} {...props}>
        {domToReact(children, createParserOptions(options))}
      </Component>
    )
  }
}

// Parser options creator
const createParserOptions = (options: any): HTMLReactParserOptions => {
  return {
    replace: (domNode: DOMNode) => {
      if (
        !('attribs' in domNode) ||
        !('children' in domNode) ||
        !('name' in domNode)
      ) {
        return undefined
      }

      const { attribs, children, name } = domNode as Element
      const textClassName = options.normalised ? 'm-0' : ''
      delete options.normalised
      const withWrapper = componentBlockWrapper(options, children as Element[])

      switch (name) {
        case 'p': {
          if (options.normalised) {
            return withWrapper(P, { normalised: true })
          }

          if (!attribs.style) {
            return undefined
          }

          if (attribs.style.includes('left')) {
            return withWrapper(P, { className: `${textClassName} text-left` })
          }

          if (attribs.style.includes('right')) {
            return withWrapper(P, { className: `${textClassName} text-right` })
          }

          return withWrapper(P, { className: `${textClassName} text-center` })
        }

        case 'ul':
          return withWrapper('ul', {
            className: `${textClassName} list-disc pl-4`,
          })

        case 'ol':
          return withWrapper('ol', {
            className: `${textClassName} list-decimal pl-4`,
          })

        case 'li':
          return withWrapper('li', { className: `${textClassName} mb-1` })

        case 'a': {
          // Check if the link contains a span element
          const hasSpan = children.some(
            (child: any) => child.type === 'tag' && child.name === 'span'
          )

          if (hasSpan) {
            return withWrapper(LinkMark, {
              href: attribs.href,
              className: `${textClassName} text-button hover:text-button-darker hover:underline`,
            })
          }

          return withWrapper(LinkMark, {
            href: attribs.href,
            className: `${textClassName} text-primary hover:underline`,
          })
        }

        case 'span': {
          return withWrapper('span')
        }

        default:
          return undefined
      }
    },
  }
}

// Main parser function
export const richTextHtmlParser = (htmlText: string, options: any = {}) => {
  if (options.withoutContainer) {
    return HTMLReactParser(
      DOMPurify && typeof DOMPurify.sanitize === 'function'
        ? DOMPurify.sanitize(htmlText)
        : htmlText,
      createParserOptions(options)
    )
  }
  return (
    <RichTextContainer color={options.color}>
      {HTMLReactParser(
        DOMPurify && typeof DOMPurify.sanitize === 'function'
          ? DOMPurify.sanitize(htmlText)
          : htmlText,
        createParserOptions(options)
      )}
    </RichTextContainer>
  )
}

export default richTextHtmlParser
